import { type Frame, GifReader } from "omggif";
import { canv } from "@/canv/global.js";
import { isBrowser, normalizeImageUrl } from "@/canv/utils";
import { IMAGE_LOAD_BORDER_COLOR, IMAGE_PREVIEW_PARAM_KEY } from "@/constants";
import { elementBase } from "@/elementBase/elementBase";
import { GIF_CACHE_NAME } from "@/elements/gif/constants";
import type {
    ForcedProps,
    GifWidgetFixState,
    GifWidgetStateByFixState,
} from "@/elements/gif/types";
import { createImage, imageLoadLimiter } from "@/elements/image";
import { widgetOption } from "@/widgetMenu/widgetMenu";

let DRAW_ERROR_LOGGED = false;

export class GifWidget extends elementBase {
    constructor(src?: string, width = 0, height = 0, playOnInit = false) {
        super();
        this.playOnInit = playOnInit;

        if (src !== undefined) {
            this.initImage(src);
        }
        this.width = width;
        this.height = height;

        this.loading = false;

        this.updateDims();
    }

    /** ElementBase методы */

    updateDims() {
        if (!isBrowser()) return;
        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    getOrInitImage() {
        if (!this.src) {
            return;
        }
        if (this.src.startsWith("data:image")) {
            // если передали base64 - показываем локальную картинку
            if (this.localImageLoading || this.localImage) {
                return this.localImage;
            }
            this.localImageLoading = true;
            createImage(this.src)
                .then((image) => {
                    this.localImage = image;
                    this.originalHeight = image.height;
                    this.originalWidth = image.width;

                    if (this.forcedProps !== undefined) {
                        this.width = this.forcedProps["miroWidth"];
                        const realRatio = this.originalHeight / this.originalWidth;
                        this.height = this.width * realRatio;

                        this.startX = this.forcedProps["miroX"] - this.width / 2;
                        this.startY = this.forcedProps["miroY"] - this.height / 2;
                        this.updateDims();
                    }
                })
                .finally(() => {
                    this.localImageLoading = false;
                });
            return this.localImage;
        }
        const token = canv.getInitDataProp("boardAccessToken");

        if (this.isPlaying && !canv.isHighPdfExporting) {
            return this.gifBitmap || this.image || this.localImage;
        }
        if (!this.imageLoading && !this.image) {
            this.imageLoading = true;
            imageLoadLimiter(() => {
                const url = new URL(this.src);
                if (token) {
                    url.searchParams.set("boardAccessToken", token);
                }
                // чтобы получить картинку для гифки - передаем параметр preview
                url.searchParams.set(IMAGE_PREVIEW_PARAM_KEY, "true");
                this.cacheName = this.src;
                return createImage(normalizeImageUrl(url.href))
                    .then((image) => {
                        this.image = image;
                        this.afterLoadImage(image, this.forcedProps);
                        this.forcedProps = undefined;

                        canv.redraw();
                    })
                    .finally(() => {
                        this.imageLoading = false;
                    });
            });
        }
        return this.image || this.localImage;
    }

    draw() {
        this.hover(this.isOver());

        try {
            canv.ctx.save();

            const image = this.getOrInitImage();
            canv.ctx.clearRect(this.startX, this.startY, this.width, this.height);

            if (!image) {
                canv.ctx.strokeStyle = IMAGE_LOAD_BORDER_COLOR;
                const BORDER_WIDTH = 1 / canv.m[0];
                canv.ctx.lineWidth = BORDER_WIDTH;
                canv.ctx.strokeRect(
                    this.startX + BORDER_WIDTH / 2,
                    this.startY + BORDER_WIDTH / 2,
                    this.width - BORDER_WIDTH,
                    this.height - BORDER_WIDTH,
                );
            }
            if (this.loading) {
                canv.ctx.globalAlpha = 0.5;
            }
            if (image) {
                canv.ctx.fillStyle = "#fff";
                canv.ctx.fillRect(this.startX, this.startY, this.width, this.height);

                canv.ctx.drawImage(image, this.startX, this.startY, this.width, this.height);
            }
            canv.ctx.restore();

            if (!this.isPlaying) {
                this.drawPreviewBadge(canv.ctx, this.startX, this.startY, this.width, this.height);
            }
        } catch (e) {
            if (!DRAW_ERROR_LOGGED) {
                DRAW_ERROR_LOGGED = true;
                canv.captureException(e);
            }
        }
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;
        this.updateDims();
    }

    resize(x: number, y: number) {
        const diffValue = (x + y) / 2;

        // Насколько надо сильнее менять высоту по сравнению с шириной, чтобы сохранить пропорции
        const hvRatio = this.height / this.width;

        this.height += hvRatio * diffValue;
        this.width += diffValue;
    }

    async beforeDeletion() {
        await this.clearCache();
        this.resetGifAnimationState();
    }

    getWidgetOptions(): widgetOption[] {
        const widgetOptions: widgetOption[] = [];
        widgetOptions.push(new widgetOption("media", "", this.UID));

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "media") {
            return this.changeImage;
        }
    }

    async changeImage(data) {
        await this.clearCache();
        this.clearPreviousImage();

        this.playOnInit = true;
        this.initImage(data["src"]);

        this.width = data["obj"].width;
        this.height = data["obj"].height;

        this.updateDims();
    }

    async clearCache() {
        const cache = await caches.open(GIF_CACHE_NAME);
        await cache.delete(this.cacheName);
    }

    getFixType(reason: string) {
        if (this.needFullState) {
            // this.needFullState = false; делать нельзя, так как getState дергается два раза для одного эвента
            // для этого instance image мы будем сохранять всегда full
            return "full";
        }

        switch (reason) {
            case "move":
                return "move";
            case "resize":
                return "resize";
            default:
                return "full";
        }
    }

    getState<F extends GifWidgetFixState>(fixType: F): GifWidgetStateByFixState<F> {
        switch (fixType) {
            case "move": {
                return {
                    startX: this.startX,
                    startY: this.startY,
                } as GifWidgetStateByFixState<F>;
            }

            case "resize": {
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                } as GifWidgetStateByFixState<F>;
            }

            case "full": {
                const state = {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "gifWidget",
                    src: this.src,
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    copyImageDataUrl: this.copyImageDataUrl || undefined,
                    // зануляем всегда, сработает в loadState при загрузке доски первым пользователем. Далее уже не будем смотреть на эти поля
                    miroX: 0,
                    miroY: 0,
                    miroWidth: 0,
                };

                return state as GifWidgetStateByFixState<F>;
            }

            default: {
                return {} as GifWidgetStateByFixState<F>;
            }
        }
    }

    loadState<F extends GifWidgetFixState>(stateObj: GifWidgetStateByFixState<F>, fixType: F) {
        switch (fixType) {
            case "move": {
                this.startX = stateObj["startX"];
                this.startY = stateObj["startY"];
                break;
            }

            case "resize": {
                this.startX = stateObj["startX"];
                this.startY = stateObj["startY"];
                this.width = stateObj["width"];
                this.height = stateObj["height"];
                break;
            }

            case "full": {
                this.UID = stateObj["UID"];
                this.parentUID = stateObj["parentUID"];

                this.startX = stateObj["startX"];
                this.startY = stateObj["startY"];
                this.width = stateObj["width"];
                this.height = stateObj["height"];

                let forcedProps: ForcedProps | undefined;
                if (
                    stateObj["miroX"] !== undefined &&
                    stateObj["miroY"] !== undefined &&
                    stateObj["miroWidth"] !== undefined &&
                    stateObj["miroWidth"] !== 0
                ) {
                    forcedProps = {
                        miroX: stateObj["miroX"],
                        miroY: stateObj["miroY"],
                        miroWidth: stateObj["miroWidth"],
                    };
                }

                this.initImage(stateObj["src"], forcedProps);

                if (stateObj["copyImageDataUrl"] != undefined) {
                    this.pasteImageDataUrl = stateObj["copyImageDataUrl"];
                }

                break;
            }
        }

        this.updateDims();
    }

    /** Image методы */

    clearPreviousImage = (keepBase64 = false) => {
        this.image = null;
        this.imageLoading = false;
        if (!keepBase64) {
            this.localImage = null;
            this.localImageLoading = false;
        }
        this.resetGifAnimationState();
    };

    initImage(src: string, forcedProps?: ForcedProps, keepBase64?: boolean) {
        this.src = src;
        // forcedProps храним, чтобы использовать в afterLoad и затем очистить
        this.forcedProps = forcedProps;
        this.clearPreviousImage(keepBase64);
    }

    afterLoadImage = (image: HTMLImageElement, forcedProps?: ForcedProps) => {
        this.originalHeight = image.height;
        this.originalWidth = image.width;

        if (forcedProps !== undefined) {
            this.width = forcedProps["miroWidth"];
            const realRatio = this.originalHeight / this.originalWidth;
            this.height = this.width * realRatio;

            this.startX = forcedProps["miroX"] - this.width / 2;
            this.startY = forcedProps["miroY"] - this.height / 2;
            this.updateDims();
            this.needFullState = true;
        }

        canv.redraw();
    };

    setLoading(loading: boolean) {
        this.loading = loading;
    }

    /** GIF методы */

    hover(isHover: boolean) {
        if (this.loading || this.isDecodingGif) {
            return;
        }
        if (isHover === this.isHover && !this.playOnInit) {
            return;
        }
        this.isHover = isHover;
        if (this.isHover || this.playOnInit) {
            this.isLooping = true;
            // При наведении запускаем декодирование или анимацию
            if (!this.reader) {
                this.initGif(this.playOnInit);
            } else if (!this.isPlaying) {
                this.startAnimation();
            }
            if (this.playOnInit) {
                this.playOnInit = false;
            }
        } else {
            this.isLooping = false;
        }
    }

    private async initGif(playOnInit: boolean) {
        this.resetGifAnimationState();
        this.isDecodingGif = true;

        try {
            const buffer = await this.getGifBuffer();

            this.reader = new GifReader(new Uint8Array(buffer));
            this.frameNumber = 0;
            this.previousFrameInfo = null;

            const width = this.reader.width;
            const height = this.reader.height;
            this.offscreen.width = width;
            this.offscreen.height = height;
            this.compositeCanvas.width = width;
            this.compositeCanvas.height = height;

            this.isDecodingGif = false;

            if (this.isHover || playOnInit) {
                this.startAnimation();
                if (playOnInit) {
                    this.isLooping = false;
                }
            }
        } catch (error) {
            console.error(error);
            canv.captureException(error);
        }
    }

    private startAnimation() {
        if (!this.compositeCtx || !this.reader) return;

        this.isPlaying = true;
        this.isLooping = true;
        this.frameNumber = 0;
        this.compositeCtx.clearRect(0, 0, this.compositeCanvas.width, this.compositeCanvas.height);
        this.drawFrame();
    }

    private updateOffscreen() {
        if (!this.ctx) return;
        this.ctx.clearRect(0, 0, this.offscreen.width, this.offscreen.height);
        this.ctx.drawImage(this.compositeCanvas, 0, 0);
    }

    private drawPreviewBadge(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
    ) {
        const badgeText = "GIF";
        const fontSize = Math.max(12, width * 0.06);
        ctx.save();
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textBaseline = "bottom";
        ctx.textAlign = "left";
        const padding = width * 0.025;
        const textWidth = ctx.measureText(badgeText).width;
        const rectWidth = textWidth + padding * 2;
        const rectHeight = fontSize + padding * 2;

        ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
        ctx.fillRect(x, y + height - rectHeight, rectWidth, rectHeight);

        // Если происходит декодирование, вычисляем анимированный цвет
        let animatedColor = "#FFF";
        if (this.isDecodingGif || this.loading) {
            // Используем время для плавного колебания от 0 до 1,
            // уменьшаем знаменатель для ускорения мерцания
            const t = 0.5 + 0.5 * Math.sin(performance.now() / 250);
            // Интерполируем между белым (255,255,255) и серым (170,170,170)
            const r = Math.round(255 * (1 - t) + 170 * t);
            const g = Math.round(255 * (1 - t) + 170 * t);
            const b = Math.round(255 * (1 - t) + 170 * t);
            animatedColor = `rgb(${r}, ${g}, ${b})`;
        }

        ctx.fillStyle = animatedColor;
        ctx.fillText(badgeText, x + padding, y + height - padding);
        ctx.restore();

        if (this.isDecodingGif || this.loading) {
            requestAnimationFrame(() => canv.redraw());
        }
    }

    private drawFrame = () => {
        if (!(this.isPlaying && this.reader && this.compositeCtx && this.ctx)) return;

        const start = performance.now();

        if (this.frameNumber === this.reader.numFrames()) {
            if (!this.isLooping) {
                this.isPlaying = false;
                this.frameNumber = 0;
                this.stopTimer();
                this.resetGifAnimationState();
                canv.redraw();
                return;
            }
            this.frameNumber = 0;
        }

        const frameInfo = this.reader.frameInfo(this.frameNumber);
        // Если frameInfo.delay 0, то используем задержку по умолчанию
        const delay = frameInfo.delay * 10 || 100;

        if (this.frameNumber === 0) {
            this.compositeCtx.clearRect(0, 0, this.reader.width, this.reader.height);
        } else if (this.previousFrameInfo) {
            // Обработка disposal-логики предыдущего кадра
            switch (this.previousFrameInfo.disposal) {
                case 0:
                case 1:
                    break;
                case 2:
                    this.compositeCtx.clearRect(
                        this.previousFrameInfo.x,
                        this.previousFrameInfo.y,
                        this.previousFrameInfo.width,
                        this.previousFrameInfo.height,
                    );
                    break;
                case 3:
                    if (this.previousImageData) {
                        this.compositeCtx.putImageData(this.previousImageData, 0, 0);
                    }
                    break;
                default:
                    console.error("Disposal method не поддерживается");
            }
        }

        // Сохраняем текущее состояние холста для возможного восстановления
        if (this.frameNumber === 0 || frameInfo.disposal < 2) {
            this.previousImageData = this.compositeCtx.getImageData(
                0,
                0,
                this.reader.width,
                this.reader.height,
            );
        }

        const imageData: ImageData = this.compositeCtx.getImageData(
            0,
            0,
            this.reader.width,
            this.reader.height,
        );
        this.reader.decodeAndBlitFrameRGBA(this.frameNumber, imageData.data);
        this.compositeCtx.putImageData(
            imageData,
            0,
            0,
            frameInfo.x,
            frameInfo.y,
            frameInfo.width,
            frameInfo.height,
        );

        this.previousFrameInfo = frameInfo;
        this.frameNumber++;

        this.updateOffscreen();
        this.gifBitmap = this.offscreen.transferToImageBitmap();
        canv.redraw();

        const end = performance.now();
        const elapsed = end - start;
        const adjustedDelay = Math.max(0, delay - elapsed);
        this.frameTimer = setTimeout(this.drawFrame, adjustedDelay);
    };

    private async getGifBuffer() {
        const cache = await caches.open(GIF_CACHE_NAME);
        const cachedResponse = await cache.match(this.cacheName);
        let buffer: ArrayBuffer;

        if (cachedResponse) {
            buffer = await cachedResponse.arrayBuffer();
        } else {
            const response = await fetch(normalizeImageUrl(this.src));
            buffer = await response.arrayBuffer();
            cache.put(
                this.cacheName,
                new Response(buffer, {
                    headers: { "Content-Type": "application/octet-stream" },
                }),
            );
        }

        return buffer;
    }

    private stopTimer() {
        if (this.frameTimer) {
            clearTimeout(this.frameTimer);
            this.frameTimer = null;
        }
    }

    private resetGifAnimationState() {
        if (this.gifBitmap) {
            this.gifBitmap.close();
        }

        this.gifBitmap = null;
        this.reader = null;
        this.isPlaying = false;
        this.isLooping = false;
        this.frameNumber = 0;
        this.previousFrameInfo = null;
        this.previousImageData = null;
        this.stopTimer();
    }

    constructorName = "gif";
    src = "";
    cacheName = "";
    width = 0;
    height = 0;
    startX = 0;
    startY = 0;
    image: HTMLImageElement | null = null;
    localImage: HTMLImageElement | null = null;

    originalWidth = 0;
    originalHeight = 0;

    isDecodingGif = false;
    isHover = false;
    loading = false;
    imageLoading = false;
    localImageLoading = false;

    copyImageDataUrl?: string;
    pasteImageDataUrl?: string;
    playOnInit: boolean;
    needFullState = false;

    private gifBitmap: ImageBitmap | null = null;
    private reader: GifReader | null = null;
    private frameNumber = 0;
    private previousFrameInfo: Frame | null = null;
    private previousImageData: ImageData | null = null;
    private frameTimer: NodeJS.Timeout | null = null;
    private isPlaying = false;
    private isLooping = false;
    private forcedProps?: ForcedProps;

    private offscreen: OffscreenCanvas = new OffscreenCanvas(1, 1);
    readonly ctx: OffscreenCanvasRenderingContext2D | null = this.offscreen.getContext("2d");
    readonly compositeCanvas: OffscreenCanvas = new OffscreenCanvas(1, 1);
    readonly compositeCtx: OffscreenCanvasRenderingContext2D | null =
        this.compositeCanvas.getContext("2d", { willReadFrequently: true });
}
